import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { Badge, Button, Card, EmptyState, Field, IconButton, Input, Spinner }
  from '../components/ui';
import { Notice, Page, PageHeader, Section, Stack } from '../components/Page';
import type { CompanyDto } from '../lib/types';

/**
 * Companies and the workspaces inside them.
 *
 * The tenant stays the billing and isolation boundary — row-level security keys on it
 * alone. A company is an organisational tier inside one customer account, which is why
 * nothing here weakens isolation: it partitions work, it does not separate customers.
 */
export function Companies() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api.companies.list() });

  const create = useMutation({
    mutationFn: () => api.companies.create({ name: name.trim() }),
    onSuccess: () => {
      setName('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (companies.isLoading) return <div className="p-8"><Spinner /></div>;

  const data = companies.data?.data ?? [];

  return (
    <Page width="wide">
      <PageHeader
        icon="building"
        title="Companies"
        description="Separate parts of your organisation. Each holds its own workspaces and
                     forms; they all share this account's billing and data boundary."
      />

      <Stack>
        {error && <Notice tone="danger">{error}</Notice>}

        <Section
          title="Add a company"
          hint="A default workspace is created alongside it, so forms have somewhere to go."
        >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[16rem] flex-1">
            <Field label="Name">
              <Input
                value={name}
                placeholder="Northern Division"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          </div>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={create.isPending || name.trim().length < 2}
          >
            {create.isPending ? 'Creating…' : 'Add company'}
          </Button>
        </div>
        </Section>

        {data.length === 0 && (
          <EmptyState
            title="No companies yet"
            description="Add one to group workspaces by part of the organisation."
          />
        )}

        {data.map((company) => <CompanyCard key={company.id} company={company} />)}
      </Stack>
    </Page>
  );
}

function CompanyCard({ company }: { company: CompanyDto }) {
  const queryClient = useQueryClient();
  const [workspaceName, setWorkspaceName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const [draft, setDraft] = useState({
    name: company.name,
    legalName: company.legalName ?? '',
    contactEmail: company.contactEmail ?? '',
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['companies'] });
    void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
  };

  const save = useMutation({
    mutationFn: () => api.companies.update(company.id, draft),
    onSuccess: () => { setEditing(false); setFailure(null); refresh(); },
    onError: (e: Error) => setFailure(e.message),
  });

  const archive = useMutation({
    mutationFn: () => api.companies.archive(company.id),
    onSuccess: () => { setConfirmArchive(false); setFailure(null); refresh(); },
    // The server refuses the default company, and its message says how to proceed.
    onError: (e: Error) => { setConfirmArchive(false); setFailure(e.message); },
  });

  const workspaces = useQuery({
    queryKey: ['workspaces', company.id],
    queryFn: () => api.workspaces.list(company.id),
  });

  const addWorkspace = useMutation({
    mutationFn: () => api.workspaces.create({
      name: workspaceName.trim(),
      companyId: company.id,
    }),
    onSuccess: () => {
      setWorkspaceName('');
      setAdding(false);
      void queryClient.invalidateQueries({ queryKey: ['workspaces', company.id] });
    },
  });

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            {company.name}
            {company.isDefault && <Badge tone="neutral">default</Badge>}
          </h2>
          <p className="mt-0.5 text-xs text-[var(--ff-text-muted)]">
            /{company.slug}
            {company.contactEmail && ` · ${company.contactEmail}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button onClick={() => setAdding((v) => !v)}>
            <Icon name="plus" size={14} /> Workspace
          </Button>
          <IconButton label={`Edit ${company.name}`} onClick={() => setEditing((v) => !v)}>
            <Icon name="text" size={15} />
          </IconButton>
          {/* Offered even on the default company: the server explains why it refuses,
              which is more useful than a control that is mysteriously absent. */}
          <IconButton
            label={`Archive ${company.name}`}
            onClick={() => setConfirmArchive(true)}
          >
            <Icon name="trash" size={15} />
          </IconButton>
        </div>
      </div>

      {failure && (
        <p role="alert" className="mb-3 rounded-md border border-[var(--ff-danger)]/35
                                   bg-[var(--ff-danger-soft)] px-3 py-2 text-xs
                                   text-[var(--ff-danger)]">
          {failure}
        </p>
      )}

      {confirmArchive && (
        <div className="mb-3 rounded-md border border-[var(--ff-danger)]/35
                        bg-[var(--ff-danger-soft)] p-3">
          <p className="text-sm font-medium">Archive {company.name}?</p>
          <p className="mt-0.5 text-xs text-[var(--ff-text-muted)]">
            It disappears from this list. Its forms and responses are untouched and stay
            reachable for export and audit.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="danger"
              onClick={() => archive.mutate()}
              disabled={archive.isPending}
            >
              {archive.isPending ? 'Archiving...' : 'Archive'}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmArchive(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {editing && (
        <div className="mb-3 grid gap-3 rounded-md border border-[var(--ff-border)]
                        bg-[var(--ff-surface-sunken)] p-3 sm:grid-cols-2">
          <Field label="Name">
            <Input value={draft.name}
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field label="Legal name" hint="If it differs from the trading name.">
            <Input value={draft.legalName}
                   onChange={(e) => setDraft({ ...draft, legalName: e.target.value })} />
          </Field>
          {/* Registration number and tax ID are on the company itself and editable through
              the API, but the list does not carry them and this card is not the place to
              ask for them — they belong on a company detail screen. */}
          <Field label="Contact email">
            <Input type="email" value={draft.contactEmail}
                   onChange={(e) => setDraft({ ...draft, contactEmail: e.target.value })} />
          </Field>
          <div className="flex items-end gap-2">
            <Button
              variant="primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || draft.name.trim().length < 2}
            >
              {save.isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {adding && (
        <div className="mb-3 flex items-end gap-3 rounded-md border border-[var(--ff-border)]
                        bg-[var(--ff-surface-sunken)] p-3">
          <div className="flex-1">
            <Field label="Workspace name">
              <Input
                value={workspaceName}
                placeholder="Customer research"
                onChange={(e) => setWorkspaceName(e.target.value)}
              />
            </Field>
          </div>
          <Button
            variant="primary"
            onClick={() => addWorkspace.mutate()}
            disabled={addWorkspace.isPending || workspaceName.trim().length < 2}
          >
            Create
          </Button>
        </div>
      )}

      {workspaces.isLoading && <Spinner label="Loading workspaces" />}

      <ul className="space-y-1.5">
        {(workspaces.data?.data ?? []).map((w) => (
          <WorkspaceRow key={w.id} workspace={w} companyId={company.id} />
        ))}
      </ul>
    </Card>
  );
}

/**
 * One workspace, renameable in place and removable when it is safe to remove.
 *
 * The remove control is always offered. The server refuses a default workspace and one
 * that still holds forms, and says which — a rule explained at the moment somebody tries
 * is worth more than a disabled button with no explanation.
 */
function WorkspaceRow({
  workspace, companyId,
}: {
  workspace: { id: string; name: string; isDefault: boolean; formCount: number };
  companyId: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(workspace.name);
  const [failure, setFailure] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['workspaces', companyId] });
    void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
  };

  const rename = useMutation({
    mutationFn: () => api.workspaces.update(workspace.id, { name: name.trim() }),
    onSuccess: () => { setEditing(false); setFailure(null); refresh(); },
    onError: (e: Error) => setFailure(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.workspaces.remove(workspace.id),
    onSuccess: () => { setFailure(null); refresh(); },
    onError: (e: Error) => setFailure(e.message),
  });

  return (
    <li className="rounded-md border border-[var(--ff-border)] px-3 py-2 text-sm">
      <div className="flex items-center gap-3">
        <Icon name="grid" size={14} />

        {editing ? (
          <>
            <Input
              className="flex-1"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') rename.mutate(); }}
            />
            <Button
              variant="primary"
              className="min-h-8 px-2 py-1 text-xs"
              onClick={() => rename.mutate()}
              disabled={rename.isPending || name.trim().length < 2}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              className="min-h-8 px-2 py-1 text-xs"
              onClick={() => { setName(workspace.name); setEditing(false); }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <span className="flex-1 truncate">{workspace.name}</span>
            {workspace.isDefault && <Badge tone="neutral">default</Badge>}
            <span className="text-xs text-[var(--ff-text-muted)]">
              {workspace.formCount} form{workspace.formCount === 1 ? '' : 's'}
            </span>
            <IconButton label={`Rename ${workspace.name}`} onClick={() => setEditing(true)}>
              <Icon name="text" size={14} />
            </IconButton>
            <IconButton
              label={`Remove ${workspace.name}`}
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              <Icon name="trash" size={14} />
            </IconButton>
          </>
        )}
      </div>

      {failure && (
        <p role="alert" className="mt-1.5 text-xs text-[var(--ff-danger)]">{failure}</p>
      )}
    </li>
  );
}
